// Статистика использования инструмента: какие страницы открывают и какие
// функции нажимают. События копятся в очереди и уходят пачкой на
// POST /api/usage/events (раз в 10 с, при 20 событиях или при уходе со
// страницы). Сервер складывает их в почасовые счётчики; смотреть — /dev/usage.
//
// Ключи:
//   page    — шаблон маршрута с вкладкой: "/regs/abandons", "/sites/:id/structure"
//             (идентификаторы заменены на ":id", вкладки оставлены как есть).
//   feature — "домен.действие" латиницей: track("regs.export_csv").
import { matchPath } from "react-router-dom";
import { authHeaders } from "./api";
import { PAGE_LABELS, PAGE_PATHS } from "./pages";

export type UsageEvent = {
  kind: "page" | "feature";
  key: string;
  ts: number;
};

// Маршруты с параметрами (из App.tsx). Остальные пути трекаются как есть.
// Порядок важен: более специфичные — выше.
const PARAM_ROUTES = [
  "/sites/:siteId/pages/:pageId",
  "/sites/:id/:tab",
  "/sites/:id",
  "/networks/:id",
  "/tracking/:id",
  "/regs/:tab",
  "/recycling/:tab",
  "/offers/:tab",
  "/mailings/:tab",
  "/finance/:tab",
  "/analysis/:tab",
  "/ads/:tab",
  "/ai-visibility/:tab",
  "/land-metrics/:tab",
  "/dev/usage/:tab",
] as const;

const PAGE_KEY_RE = /^\/[a-z0-9/_:.-]{0,120}$/;
const ID_LIKE = /^(?:[a-z0-9]{16,}|\d+)$/i;

// Нормализует путь в ключ страницы: вкладки остаются, идентификаторы → ":id".
export function pageKeyOf(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  for (const pattern of PARAM_ROUTES) {
    const m = matchPath(pattern, path);
    if (!m) continue;
    const key = pattern
      .split("/")
      .map((seg) => {
        if (!seg.startsWith(":")) return seg;
        const name = seg.slice(1);
        if (name === "tab") return (m.params.tab ?? "").toLowerCase();
        return ":id";
      })
      .join("/");
    return PAGE_KEY_RE.test(key) ? key : null;
  }
  const key = path
    .split("/")
    .map((seg) => (ID_LIKE.test(seg) ? ":id" : seg.toLowerCase()))
    .join("/");
  return PAGE_KEY_RE.test(key) ? key : null;
}

// --- очередь ---------------------------------------------------------------

const FLUSH_MS = 10_000;
const FLUSH_AT = 20;
const MAX_QUEUE = 200;

let queue: UsageEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let lastPageKey: string | null = null;
let enabled = true;

function schedule() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flushUsage();
  }, FLUSH_MS);
}

function push(e: UsageEvent) {
  if (!enabled) return;
  if (queue.length >= MAX_QUEUE) queue = queue.slice(-MAX_QUEUE + 1);
  queue.push(e);
  if (queue.length >= FLUSH_AT) void flushUsage();
  else schedule();
}

// Отправляет очередь. keepalive — чтобы запрос дожил при закрытии вкладки.
export async function flushUsage(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  try {
    await fetch("/api/usage/events", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { ...authHeaders(), "content-type": "application/json" },
      body: JSON.stringify({ events }),
    });
  } catch {
    // Статистика не стоит шума: потерянная пачка — не ошибка для пользователя.
  }
}

// Просмотр страницы: вызывается трекером на каждую смену pathname.
export function trackPage(pathname: string): void {
  const key = pageKeyOf(pathname);
  if (!key) return;
  if (key === lastPageKey) return; // ре-рендер того же пути — не визит
  lastPageKey = key;
  push({ kind: "page", key, ts: Date.now() });
}

// Явное действие пользователя: track("regs.export_csv").
export function track(key: string): void {
  if (!/^[a-z0-9._-]{1,80}$/.test(key)) return;
  push({ kind: "feature", key, ts: Date.now() });
}

export function setUsageEnabled(v: boolean): void {
  enabled = v;
}

let bound = false;
export function bindUsageLifecycle(): void {
  if (bound || typeof window === "undefined") return;
  bound = true;
  window.addEventListener("pagehide", () => void flushUsage());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushUsage();
  });
}

// --- подписи для dev-страницы ----------------------------------------------

const PATH_TO_PAGE = new Map(PAGE_PATHS.map((p) => [p.path, p.page] as const));

// Человеческие названия вкладок (ключ вкладки → подпись). Неизвестные
// показываются как есть.
const TAB_LABELS: Record<string, string> = {
  overview: "Обзор",
  table: "Таблица",
  regs: "Реги",
  "lead-intake": "Телеметрия",
  abandons: "Брошенные заявки",
  "cc-regs": "CC-реги",
  networks: "Сети",
  bans: "Баны",
  probe: "Тест-реги",
  syncs: "Синхронизации",
  candidates: "Кандидаты",
  stats: "Статистика",
  "reg-config": "Настройка регистраций",
  "offer-targets": "Литьё на оффер",
  recycles: "Авторесайклы",
  "click-links": "Клик-ссылки",
  "product-types": "Тип товара",
  offers: "Все офферы",
  products: "Товары",
  cards: "Карточки",
  geo: "Товары и гео",
  rates: "Ставки",
  "free-domains": "Свободные домены",
  domains: "Домены",
  send: "Рассылка",
  campaigns: "Кампании",
  triggers: "Триггеры",
  logs: "Логи",
  cohorts: "Когорты",
  buy: "Баинг",
  brands: "Наши бренды",
  discover: "Чужие товары",
  search: "Поиск",
  advertisers: "Рекламодатели",
  watches: "Наблюдения",
  sites: "Сайты",
  pages: "Страницы",
  bing: "Bing",
  import: "Импорт",
  fetch: "Обращения",
  speed: "Скорость",
  errors: "Ошибки JS",
  google: "Google",
  direct: "Прямые заходы",
  ai: "ИИ",
  social: "Соцсети",
  ref: "Сайты и метки",
  gsc: "GSC проблемы",
  queries: "Запросы",
  crawl: "Граф ссылок",
  structure: "Структура",
  features: "Функции",
  users: "Пользователи",
};

const EXTRA_LABELS: Record<string, string> = {
  "/networks": "Сети",
  "/networks/:id": "Сеть",
  "/sites/:id": "Сайт",
  "/sites/:id/pages/:id": "Страница сайта",
  "/tracking/:id": "Отслеживание · детали",
  "/access": "Доступы",
  "/dev/usage": "Использование",
};

// "/finance/cohorts" → "Финансы · Когорты"; "/sites/:id/regs" → "Сайт · Реги".
export function pageLabelOf(key: string): string {
  if (EXTRA_LABELS[key]) return EXTRA_LABELS[key];
  const page = PATH_TO_PAGE.get(key);
  if (page) return PAGE_LABELS[page];
  const segs = key.split("/").filter(Boolean);
  if (segs.length === 0) return PAGE_LABELS.dashboard;
  const base = "/" + segs[0];
  const baseLabel =
    EXTRA_LABELS[base] ??
    (PATH_TO_PAGE.get(base) ? PAGE_LABELS[PATH_TO_PAGE.get(base)!] : segs[0]);
  const rest = segs
    .slice(1)
    .filter((s) => s !== ":id")
    .map((s) => TAB_LABELS[s] ?? s);
  return [baseLabel, ...rest].join(" · ");
}

// Все известные верхнеуровневые страницы — чтобы показать «не открывали».
export function knownPageKeys(): string[] {
  return PAGE_PATHS.map((p) => p.path);
}
